import Axios, {
  AxiosInstance,
  ResponseType,
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '@/app/shared/stores/auth-store';
import { oktaAuth } from '@/app/shared/okta/auth';
import { useAlertStore } from '@/app/shared/stores/alert-store';
import { Motif } from '@/app/shared/enums/motif-enum';
import { APIError } from '@/app/shared/types/api/api-error.interface';

const withAuthInterceptor = (
  axiosInstance: AxiosInstance,
  isExchangeTokenServiceCall: boolean
): AxiosInstance => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const isLoginRequest = config.url?.includes('/auth/login');
      const isprofileCheckRequest = config.url?.includes(
        '/auth/get-login-profile'
      );
      const authStore = useAuthStore();

      if (isLoginRequest || isprofileCheckRequest) {
        return config;
      }
      if (authStore.shouldUseOktaLogin) {
        if (config.headers) {
          const oktaToken = oktaAuth.getAccessToken();

          if (!isExchangeTokenServiceCall && authStore.useApigeeFlow()) {
            let exchangeServiceToken = authStore.exchangeServiceToken;

            if (!exchangeServiceToken) {
              return authStore.getExchangeServiceToken().then(() => {
                config.headers!['Authorization'] =
                  'Bearer ' + authStore.exchangeServiceToken;
                config.headers!['ATB-Authorization'] = 'Bearer ' + oktaToken;
                return config;
              });
            } else {
              config.headers['Authorization'] =
                'Bearer ' + exchangeServiceToken;
              config.headers['ATB-Authorization'] = 'Bearer ' + oktaToken;
            }
          } else {
            config.headers['Authorization'] = 'Bearer ' + oktaToken;
          }
        }
      } else {
        if (authStore.formAuthJwtAccessToken) {
          config.headers!['Authorization'] =
            'Bearer ' + authStore.formAuthJwtAccessToken;
        }
      }
      return config;
    }
  );

  return axiosInstance;
};

const withErrorInterceptor = (axiosInstance: AxiosInstance): AxiosInstance => {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<APIError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };
      const authStore = useAuthStore();

      const isAuthEndpoint =
        originalRequest.url?.includes('/auth/refresh-token') ||
        originalRequest.url?.includes('/auth/logout') ||
        originalRequest.url?.includes('/auth/login');

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        authStore.shouldUseFormLogin &&
        !isAuthEndpoint
      ) {
        originalRequest._retry = true;
        const refreshed = await authStore.refreshFormAccessToken();
        if (refreshed) {
          originalRequest.headers['Authorization'] =
            'Bearer ' + authStore.formAuthJwtAccessToken;
          return axiosInstance(originalRequest);
        }
      }

      const alertStore = useAlertStore();
      if (error.response) {
        if (!isAuthEndpoint) {
          alertStore.showNotification(
            error.response.data?.message ||
              'An error occurred while processing your request.',
            'Server',
            Motif.ERROR
          );
        }
        return Promise.reject(error.response.data);
      } else {
        return Promise.reject(error);
      }
    }
  );
  return axiosInstance;
};

class AxiosProvider {
  private atbAmsApiEndpoint: string = config.VUE_APP_ATB_AMS_SERVICE_URL;
  private atbAdminApiEndpoint: string = config.VUE_APP_ATB_ADMIN_SERVICE_URL;
  private exchangeTokenServiceEndpoint: string =
    config.VUE_APP_TOKEN_EXCHANGE_SERVICE_URL;

  private instancesMap: Record<string, AxiosInstance> = {};

  private AMS_AXIOS_INSTANCE_KEY: string = 'ams-axios-instance';
  private ADMIN_AXIOS_INSTANCE_KEY: string = 'admin-axios-instance';
  private EXCHANGE_TOKEN_SERVICE_AXIOS_INSTANCE_KEY: string =
    'exchange-token-service-axios-instance';

  public get amsApiClient(): AxiosInstance {
    if (!this.atbAmsApiEndpoint || this.atbAmsApiEndpoint == '') {
      throw new Error('Config VUE_APP_ATB_SERVICE_URL is missing');
    } else {
      if (!this.instancesMap[this.AMS_AXIOS_INSTANCE_KEY]) {
        const requestConfig: AxiosRequestConfig = {
          baseURL: (this.atbAmsApiEndpoint || '').replace(/[\\/]+$/, ''),
          // timeout: 1000,
          responseType: 'json' as ResponseType,
          headers: {
            'Content-Type': 'application/json',
          },
          xsrfCookieName: 'XSRF-TOKEN',
          xsrfHeaderName: 'X-XSRF-TOKEN',
          withCredentials: true,
        };
        this.instancesMap[this.AMS_AXIOS_INSTANCE_KEY] = withErrorInterceptor(
          withAuthInterceptor(Axios.create(requestConfig), false)
        );
      }
      return this.instancesMap[this.AMS_AXIOS_INSTANCE_KEY];
    }
  }

  public get adminApiClient(): AxiosInstance {
    if (!this.atbAdminApiEndpoint || this.atbAdminApiEndpoint == '') {
      throw new Error('Config VUE_APP_ATB_ADMIN_SERVICE_URL is missing');
    } else {
      if (!this.instancesMap[this.ADMIN_AXIOS_INSTANCE_KEY]) {
        const requestConfig: AxiosRequestConfig = {
          baseURL: (this.atbAdminApiEndpoint || '').replace(/[\\/]+$/, ''),
          // timeout: 1000,
          responseType: 'json' as ResponseType,
          headers: {
            'Content-Type': 'application/json',
          },
          xsrfCookieName: 'XSRF-TOKEN',
          xsrfHeaderName: 'X-XSRF-TOKEN',
          withCredentials: true,
        };
        this.instancesMap[this.ADMIN_AXIOS_INSTANCE_KEY] = withErrorInterceptor(
          withAuthInterceptor(Axios.create(requestConfig), false)
        );
      }
      return this.instancesMap[this.ADMIN_AXIOS_INSTANCE_KEY];
    }
  }

  public get exchangeTokenServiceClient(): AxiosInstance {
    if (
      !this.exchangeTokenServiceEndpoint ||
      this.exchangeTokenServiceEndpoint == ''
    ) {
      throw new Error('Config VUE_APP_TOKEN_EXCHANGE_SERVICE_URL is missing');
    } else {
      if (!this.instancesMap[this.EXCHANGE_TOKEN_SERVICE_AXIOS_INSTANCE_KEY]) {
        const requestConfig: AxiosRequestConfig = {
          baseURL: (this.exchangeTokenServiceEndpoint || '').replace(
            /[\\/]+$/,
            ''
          ),
          // timeout: 1000,
          responseType: 'json' as ResponseType,
          headers: {
            'Content-Type': 'application/json',
            Scope: 'read',
          },
        };
        this.instancesMap[this.EXCHANGE_TOKEN_SERVICE_AXIOS_INSTANCE_KEY] =
          withErrorInterceptor(
            withAuthInterceptor(Axios.create(requestConfig), true)
          );
      }
      return this.instancesMap[this.EXCHANGE_TOKEN_SERVICE_AXIOS_INSTANCE_KEY];
    }
  }
}

export default new AxiosProvider();
